import { Container } from "@red-digital/bricks";
import cn from "classnames";
import React from "react";
import { Tile } from "../Tile";

import { TypeItems_containerWithoutUnresolvableLinksResponse } from "@2u-landing-pages/types";
import styles from "./ItemsContainer.module.scss";

const spacingMap: Record<string, string> = {
  "Large (24/16)": "large",
  "Medium (16/12)": "medium",
  "Small (8/4)": "small",
  "None (0/0)": "none",
};

interface ItemsContainerProps {
  orientation: "horizontal" | "vertical";
  layoutOrientation?: "grid" | "stacked";
  stackedLayoutPadding?: "Large (24/16)" | "Medium (16/12)" | "None (0/0)" | "Small (8/4)";
  className?: string;
  entry: TypeItems_containerWithoutUnresolvableLinksResponse;
  parseMetadata?: (template: string) => string;
}

interface CustomCSSProperties extends React.CSSProperties {
  "--tile-background-color"?: string;
}

export const ItemsContainer: React.FC<ItemsContainerProps> = ({
  orientation,
  layoutOrientation = "grid",
  stackedLayoutPadding = "Large (24/16)",
  className,
  entry,
  parseMetadata,
}) => {
  const style: CustomCSSProperties = {};

  const itemsBgColor = entry?.fields?.items_background_color;
  if (itemsBgColor) style["--tile-background-color"] = `var(${itemsBgColor})`;

  const mobileTextOrientation = entry?.fields?.mobile_item_orientation || orientation;
  const desktopTextOrientation = entry?.fields?.desktop_item_orientation || orientation;
  const mobileLayoutOrientation = entry?.fields?.mobile_layout_orientation || layoutOrientation;
  const desktopLayoutOrientation = entry?.fields?.desktop_layout_orientation || layoutOrientation;
  const selectedStackedPadding = entry?.fields?.stacked_layout_padding || stackedLayoutPadding;

  const badge = entry?.fields?.items?.some((item) => item?.fields?.variation === "badge");
  const divider = entry?.fields?.items?.some((item) => item?.fields?.variation === "divider");
  return (
    <Container
      flex
      wrap="wrap"
      justifyContent="center"
      lg={{ justifyContent: orientation === "vertical" ? "center" : "flex-start" }}
      className={cn(styles.itemsContainer, className, {
        [styles.badgeWrapper]: badge,
        [styles.dividerWrapper]: divider,
        [styles[`spacing-mobile-${spacingMap[selectedStackedPadding]}`]]: mobileLayoutOrientation === "stacked",
        [styles[`spacing-desktop-${spacingMap[selectedStackedPadding]}`]]: desktopLayoutOrientation === "stacked",
      })}
      style={style}
    >
      {entry?.fields?.items?.map((item, index) => {
        if (!item) return null;

        return (
          <Tile
            key={index}
            variation={item?.fields?.variation}
            padded={Boolean(itemsBgColor)}
            desktopOrientation={desktopTextOrientation}
            mobileOrientation={mobileTextOrientation}
            desktopLayoutOrientation={desktopLayoutOrientation}
            mobileLayoutOrientation={mobileLayoutOrientation}
            stackedLayoutPadding={selectedStackedPadding}
            content={item}
            parseMetadata={parseMetadata}
            className={cn(styles.tile, {
              [styles.badge]: item?.fields?.variation === "badge",
              [styles.divider]: item?.fields?.variation === "divider",
              [styles.tileDesktop]: orientation === "vertical",
              [styles.stackedMobile]: mobileLayoutOrientation === "stacked",
              [styles.stackedDesktop]: desktopLayoutOrientation === "stacked",
            })}
          />
        );
      })}
    </Container>
  );
};
